/** @jsx jsx */

import _ from 'lodash';
import React, { Component, ReactElement } from 'react';
import { $apply, $each, $none, $set, update } from 'qim';

import { jsx } from '@emotion/core';

import EditorWithPreviewLayout from 'app/developer-v3/layouts/EditorWithPreviewLayout';
import { PLACEHOLDERS_FIELDS } from 'app/developer-v3/constants';
import { cleanObjectOfEmptyValues } from 'app/common/CommonUtils';
import { getFormFields } from 'app/developer-v3/pages/actions/ActionInputs/fieldTemplates';
import {
  convertDeepLinkMetaKeyToCamelCase,
  createValidator,
  setReadOnly,
  findByKeyOrIndex,
  getDeepLinkMetaInfoFromQuery,
  getInputFields,
  getPerformFromService,
  isNewServiceField,
  normalizeField,
  transformInputToFormatic,
  transformInputToSchema,
  updateServiceInputFields,
  validateDisallowed,
  validateDynamicDropdown,
  validateMin,
  validateRequired,
  validateSchemaKey,
} from 'app/developer-v3/utils';

import CTA from './CTA';

import type {
  ErrorList,
  ValidatorFormValues,
} from 'app/developer-v3/types/validator';
import type {
  ActionInputParams,
  ActionInputType,
  ActionSchema,
  FormFieldTemplate,
  FormifiedFieldSchema,
  QueryHighlight,
} from 'app/developer-v3/types';
import type {
  FieldOrFunctionSchema,
  StaticDropdown,
} from 'app/developer-v3/platformSchema/field';

const formifyFoundInputField = (
  inputFields: FieldOrFunctionSchema[],
  selector: string | number
): FieldOrFunctionSchema | {} => {
  return transformInputToFormatic(
    findByKeyOrIndex(inputFields, selector) || {}
  );
};

type Props = {
  action: ActionSchema;
  children: ReactElement;
  isActionSetup: boolean;
  isReadOnly: boolean;
  saveInputField: (action: ActionSchema) => void;
  params: ActionInputParams;
  query: QueryHighlight;
};

type State = {
  currentAction: ActionSchema;
  formifiedField: FormifiedFieldSchema | {};
  errors: ErrorList;
  attemptedSave: boolean;
  inputFormFields: FormFieldTemplate[];
  validateValues?: (values: ValidatorFormValues) => ErrorList;
};

class ActionInputs extends Component<Props, State> {
  state = {
    currentAction: this.props.action,
    formifiedField: {},
    errors: {},
    attemptedSave: false,
    inputFormFields: [],
    validateValues: undefined,
  };

  get inputFields(): Array<FieldOrFunctionSchema> {
    return getInputFields(this.state.currentAction);
  }

  componentDidUpdate(prevProps: Props) {
    const prevPerform = getPerformFromService(prevProps.action);
    const perform = getPerformFromService(this.props.action);

    if (!_.isEqual(prevPerform, perform)) {
      this.setState({ currentAction: this.props.action });
    }
  }

  get disallowedKeys() {
    const fields = this.inputFields;
    const field = formifyFoundInputField(fields, this.props.params.inputKey);

    if (!('key' in field)) {
      return _.map(fields, 'key');
    }

    const currentFieldKey = field.key;

    return _.chain(fields).reject({ key: currentFieldKey }).map('key').value();
  }

  refreshValidation = (inputType: ActionInputType = 'default') => {
    const inputFormFields = setReadOnly(
      getFormFields(inputType, this.disallowedKeys),
      this.props.isReadOnly
    );
    const validateValues = createValidator(inputFormFields, [
      validateMin,
      validateSchemaKey,
      validateDynamicDropdown,
      validateRequired,
      validateDisallowed,
    ]);

    return { inputFormFields, validateValues };
  };

  setDropdown = (type: string, value: string | StaticDropdown) => {
    this.editFieldAndAction({
      ..._.omit(
        this.state.formifiedField,
        type === 'dynamic' ? ['choices'] : ['dynamic', 'search']
      ),
      [type]: type === 'dynamic' ? cleanObjectOfEmptyValues(value) : value,
    });
  };

  toggleDropdown = (usingDropdown: boolean) => {
    if (!usingDropdown) {
      this.editFieldAndAction(
        _.omit(this.state.formifiedField, ['dynamic', 'choices', 'search'])
      );
    }
  };

  setSearch = (_type: string, searchRef: string) => {
    const formValues: FormifiedFieldSchema = {
      ...this.state.formifiedField,
      search: searchRef,
    };
    this.editFieldAndAction(formValues);
  };

  toggleSearch = (usesSearch: boolean) => {
    if (!usesSearch) {
      this.editFieldAndAction(_.omit(this.state.formifiedField, ['search']));
    }
  };

  editFieldAndAction = (formValues: FormifiedFieldSchema) => {
    const { inputKey, childInputKey } = this.props.params;

    const formifiedField = normalizeField(formValues, true);
    const schemafiedField = transformInputToSchema(formifiedField);
    const cleanSchemafiedField = { ..._.omit(schemafiedField, ['children']) };

    const inputFields = this.inputFields;
    const inputField = isNewServiceField(inputKey)
      ? _.last(inputFields)
      : findByKeyOrIndex(inputFields, inputKey);

    const nextInputFields = update(
      [
        $each,
        fd => fd === inputField,
        $apply(fd => {
          const childInputFields = fd.children;
          if (!childInputKey || !childInputFields) return schemafiedField;

          const childField = isNewServiceField(childInputKey)
            ? _.last(childInputFields)
            : findByKeyOrIndex(childInputFields, childInputKey);
          const children = update(
            [$each, cfd => cfd === childField, $set(cleanSchemafiedField)],
            childInputFields
          );
          return { ...fd, children };
        }),
      ],
      inputFields
    );

    const action = this.refreshAction(nextInputFields);
    const errors = this.validateInputField(formifiedField);

    this.setState({ currentAction: action, errors, formifiedField });
  };

  setInputField = (
    inputKey: string | number,
    inputType: ActionInputType = 'default',
    childInputKey?: string | number
  ) => {
    const newState: State = { ...this.state };

    newState.attemptedSave = false;

    const validation = this.refreshValidation(inputType);
    newState.inputFormFields = validation.inputFormFields;
    newState.validateValues = validation.validateValues;

    const inputFields = this.inputFields;

    if (isNewServiceField(inputKey)) {
      if (childInputKey) {
        if (isNewServiceField(childInputKey)) {
          const newChildInputField = PLACEHOLDERS_FIELDS.default.create();

          if (this.hasParentInputFieldCreated()) {
            const lastField = _.last(inputFields);
            const childInputFields =
              lastField && 'children' in lastField
                ? lastField.children || []
                : [];
            const newChildInputFields = [
              ...childInputFields,
              newChildInputField,
            ];
            const newInputField = {
              ...lastField,
              children: newChildInputFields,
            };
            const newInputFields = [
              ..._.initial(inputFields),
              newInputField,
            ] as FieldOrFunctionSchema[];

            newState.currentAction = this.refreshAction(newInputFields);
          } else {
            const newInputField = PLACEHOLDERS_FIELDS[inputType].create();
            const newChildInputFields = [newChildInputField];
            newInputField.children = newChildInputFields;
            const newInputFields = inputFields.concat(newInputField);

            newState.currentAction = this.refreshAction(newInputFields);
          }

          newState.formifiedField = transformInputToFormatic(
            newChildInputField
          );
        } else {
          const inputField = _.last(inputFields);
          const childInputFields = _.get(inputField, 'children', []);
          const childInputField = findByKeyOrIndex(
            childInputFields,
            childInputKey
          );

          // TODO: redirect?
          if (!childInputField) return;

          newState.formifiedField = transformInputToFormatic(childInputField);
        }
      } else {
        if (this.hasParentInputFieldCreated()) {
          newState.currentAction = this.refreshAction(inputFields);
          newState.formifiedField = transformInputToFormatic(
            _.last(inputFields)
          );
        } else {
          const newInputField = PLACEHOLDERS_FIELDS[inputType].create();
          const newInputFields = inputFields.concat(newInputField);
          newState.currentAction = this.refreshAction(newInputFields);
          newState.formifiedField = transformInputToFormatic(newInputField);
        }
      }
    } else {
      const inputField = findByKeyOrIndex(inputFields, inputKey);

      // TODO: redirect?
      if (!inputField) return;

      if (childInputKey) {
        const childInputFields = _.get(inputField, 'children');

        if (isNewServiceField(childInputKey)) {
          const newChildInputField = PLACEHOLDERS_FIELDS.default.create();
          const newChildInputFields = childInputFields.concat(
            newChildInputField
          );
          const newInputField = {
            ...inputField,
            children: newChildInputFields,
          };
          const newInputFields = update(
            [$each, element => element === inputField, $set(newInputField)],
            inputFields
          );

          newState.currentAction = this.refreshAction(newInputFields);
          newState.formifiedField = transformInputToFormatic(
            newChildInputField
          );
        } else {
          const childInputField = findByKeyOrIndex(
            childInputFields,
            childInputKey
          );

          // TODO: redirect?
          if (!childInputField) return;

          newState.formifiedField = transformInputToFormatic(childInputField);
        }
      } else {
        newState.formifiedField = transformInputToFormatic(inputField);
      }
    }

    this.setState(newState);
  };

  resetUnsavedEdits = () =>
    this.setState({
      currentAction: this.props.action,
      attemptedSave: false,
      formifiedField: {},
    });

  isFormValid = (errors: ErrorList = this.state.errors) => {
    return _.isEmpty(errors);
  };

  saveChanges = (skipConfirm: boolean | {} = false) => {
    if (skipConfirm === true) {
      this.props.saveInputField(this.state.currentAction);
      return;
    }

    const { formifiedField }: FormifiedFieldSchema = this.state;
    const errors =
      formifiedField.source || _.isEmpty(formifiedField)
        ? {}
        : this.validateInputField(formifiedField);

    if (this.isFormValid(errors)) {
      this.setState({}, async () => {
        await this.props.saveInputField(this.state.currentAction);
        this.setState({
          attemptedSave: false,
          ...this.refreshValidation(),
        });
      });
    } else {
      this.setState({ attemptedSave: true, errors });
    }
  };

  updateInputFields = (inputFields: FieldOrFunctionSchema[]) =>
    this.setState({ currentAction: this.refreshAction(inputFields) });

  deleteInputField: {
    (inputFieldSelector: number): void;
    (inputFieldSelector: string, childFieldIndex: number): void;
  } = (inputFieldSelector: any, childFieldIndex?: number) => {
    const inputFields = this.inputFields;

    if (childFieldIndex === undefined) {
      const newInputFields = update(
        [parseInt(inputFieldSelector, 10), $none],
        inputFields
      );

      this.setState(
        { currentAction: this.refreshAction(newInputFields) },
        () => {
          this.setState(this.refreshValidation());
          this.saveChanges(true);
        }
      );
    } else {
      const inputField = isNewServiceField(inputFieldSelector)
        ? _.last(inputFields)
        : findByKeyOrIndex(inputFields, inputFieldSelector);

      if (!inputField || !inputField.key) {
        return;
      }

      inputFieldSelector = inputField.key;

      const newInputFields = update(
        [
          $each,
          fd => fd === inputField,
          $apply(fd => {
            const newChildInputFields = fd.children.filter(
              (_cfd, i) => i !== childFieldIndex
            );
            return { ...fd, children: newChildInputFields };
          }),
        ],
        inputFields
      );

      this.setState({
        currentAction: this.refreshAction(newInputFields),
        formifiedField: formifyFoundInputField(
          newInputFields,
          inputFieldSelector
        ),
      });
    }
  };

  refreshAction = (inputFields: FieldOrFunctionSchema[]) => {
    return updateServiceInputFields(this.state.currentAction, inputFields);
  };

  hasParentInputFieldCreated = () => {
    const savedInputFields = _.get(
      this.props.action,
      ['operation', 'inputFields'],
      []
    );
    const inputFields = _.get(
      this.state.currentAction,
      ['operation', 'inputFields'],
      []
    );

    return savedInputFields.length !== inputFields.length;
  };

  validateInputField = (formifiedField: ValidatorFormValues): ErrorList =>
    this.state.validateValues && !formifiedField.source
      ? this.state.validateValues(formifiedField)
      : {};

  isSaveDisabled = () => {
    if (this.props.isReadOnly) return true;

    if (!this.state.attemptedSave) return false;

    return !this.isFormValid();
  };

  render() {
    const deepLinkInfo = getDeepLinkMetaInfoFromQuery(this.props.query);

    const metaInfo =
      deepLinkInfo &&
      convertDeepLinkMetaKeyToCamelCase(deepLinkInfo, 'help_text');

    return (
      <EditorWithPreviewLayout
        service={this.state.currentAction}
        title="Form Editor"
      >
        <CTA isSetup={this.props.isActionSetup} />
        {React.cloneElement(React.Children.only(this.props.children), {
          action: this.state.currentAction,
          attemptedSave: this.state.attemptedSave,
          deleteInputField: this.deleteInputField,
          errors: this.state.errors,
          formFields: this.state.inputFormFields,
          formifiedField: this.state.formifiedField,
          info: metaInfo,
          isFormValid: this.isFormValid(),
          isSaveDisabled: this.isSaveDisabled,
          onFormChange: this.editFieldAndAction,
          resetUnsavedEdits: this.resetUnsavedEdits,
          saveActionChanges: this.saveChanges,
          setDropdown: this.setDropdown,
          setInputField: this.setInputField,
          setSearch: this.setSearch,
          toggleDropdown: this.toggleDropdown,
          toggleSearch: this.toggleSearch,
          updateInputFields: this.updateInputFields,
        })}
      </EditorWithPreviewLayout>
    );
  }
}

export default ActionInputs;
